import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  computed,
  effect,
  inject,
  signal,
  untracked,
} from '@angular/core';
import { FieldType, FormlyFieldConfig } from '@ngx-formly/core';
import { FormControl, FormGroup } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { forkJoin, Observable } from 'rxjs';

import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDialog } from '@angular/material/dialog';
import { FuseAlertComponent } from '@fuse/components/alert';
import { DocumentFileActionsService } from '../../services/document-file-actions.service';
import { openHistoryDialog } from '../history/history.component';
import { LabelListComponent } from '../label-list.component';
import { DocumentCardComponent } from '../documents/document-card.component';
import type { DocumentCardDensity, DocumentCardFile, DocumentCardMenu, DocumentCardMetaRow } from '../documents/document-card.types';
import { StatusBadgeComponent } from '../status-badge.component';
import { ReviewFlagButtonComponent } from '../review-flag/review-flag-button.component';
import { ReviewFlagPanelComponent } from '../review-flag/review-flag-panel.component';
import type { ReviewFlag, WizardPageFieldTree } from '../review-flag/review-flags.types';
import {
  DEFAULT_SECTION_REASON,
  composeDeclineMessage,
  defaultDocumentReason,
  flagReason,
  hostsDocumentsChecklist,
  isFlagResolved,
  isFlagged,
  openFlags,
  pageKeyOf,
  pageTarget,
  readFlags,
  resolveFlags,
  setFlagReason,
  toggleFlag,
} from '../review-flag/review-flags.util';
import { ModulesStore } from '../../../features/admin/modules/modules.store';
import { DETAILS_SERVICE_TOKEN } from '../../../features/admin/modules/modules-entity/templates/details/details/details.service';
import { EntriesService } from '../../../features/admin/modules/features/entries/entries.service';
import { FormService } from '../../../features/admin/modules/features/form-builder/form.service';
import { canReadFieldRight, effectiveFieldRightForHierarchy } from '../../../features/admin/modules/field-rights.util';
import { UtilityService } from '../../services/utility.service';
import { UserStore } from '../../../core/services/user.store';
import type { Entry, Module } from '../../../features/admin/modules/modules.model';
import {
  ONBOARDING_STATUS_SLUGS,
  type AnswerRow,
  type AnswerSection,
  type ApplicantField,
  type PendingReviewWrite,
  type ProcessedPage,
} from './readonly-onboarding.types';
import type { TaxonomyOption } from './readonly/readonly.types';
import { hasHiddenClass } from './readonly/overview.types';
import { debugLog } from '../../utils/debug-logger';
import { entryHasFile, resolveDocumentFileName, resolveDocumentTitle } from '../../utils/document-file-meta';
import { CONTAINER_TYPES, formatDisplayValue } from './review-value.util';
import { createTaxonomyOptionResolver } from './readonly/display-context.util';
import { deriveCardBadge, type DocumentCardBadge } from '../../utils/document-card-status';
import { forEachInFieldTree } from './field-tree.util';
import { documentBelongsToReview, reviewDocumentsFilter } from '../../utils/onboarding-checklist-templates';
import { ReviewChecklistTemplatesService } from './review-checklist-templates.service';
import { ReviewDocumentsQueryService } from './review-documents-query.service';
import { ReviewWriteRegistry } from '../review-flag/review-write-registry.service';
import { ReviewFlagPersistenceService } from '../review-flag/review-flag-persistence.service';

/**
 * Field types that render content but never carry a reviewable answer, for THIS component's
 * overview only.
 *
 * Deliberately NOT derived from `isDisplayOnlyFieldType` (`display-only-field-types.ts`): that
 * catalogue answers "does this type take input", which is a different (and broader) question than
 * "does this type carry an answer worth showing here". `readonly_object`, `readonly_entry`,
 * `readonly-input` and `readonly_onboarding` are display-only in that sense (no control ever
 * writes through them) but DO carry reviewable data — that file's own doc comment says it for
 * `readonly_onboarding`: "DOES own a compound value (`entry.review`), but it has no input of its
 * own ... 'Display-only' here means 'takes no input', not 'carries no data'". Deriving this gate
 * from `isDisplayOnlyFieldType` silently dropped those answer rows from the reviewer's overview.
 *
 * This stays a hand-picked list of the types that print nothing reviewable at all: chrome
 * (`title`/`text`/`divider`/`fuse-alert`), the interface-action button, and the document
 * checklist, whose uploads are reviewed separately through section 3's document cards.
 */
const READONLY_ONBOARDING_NON_ANSWER_TYPES = new Set([
  'fuse-alert',
  'interface-action-button',
  'divider',
  'title',
  'text',
  'required-documents-checklist',
]);

/** True for a field type that renders content but never carries a reviewable answer. */
function isNonAnswerFieldType(type: string): boolean {
  return READONLY_ONBOARDING_NON_ANSWER_TYPES.has(type);
}

/**
 * Is this `compareField` an ID reference rather than a keyPath?
 *
 * Both keyPath resolvers this component emulates read against an Entry envelope —
 * `hideConditionalFunction` does `keyPathToValue({ entry: <root form value>, id: undefined }, …)`
 * and falls back to `keyPathToValue(formState.entry, …)` — so every keyPath the form-builder
 * emits is the dotted `entry.…` shape of `props.keyPath`, and the one meaningful bare path is
 * `id`. A single segment that is not `id` can therefore only be a persisted Formly id,
 * reachable ONLY through the live tree's `findFieldValueById`, which the static probe does not
 * have. The 'formly_' prefix is NOT the discriminant: the form-builder keeps author-set ids
 * verbatim, and such an id used to slip past and be evaluated as a keyPath — resolving to
 * undefined and hiding an answer the applicant really gave.
 */
function isIdReference(compareField: string): boolean {
  const normalized = UtilityService.normalizeKeyPath(compareField);
  return !normalized.includes('.') && normalized !== 'id';
}

/**
 * The sub-keys this field owns inside its OWN entry value. They are declared once, as the field
 * type's sub-fields (`ONBOARDING_REVIEW_FIELDS` in fieldGroup.config.ts), so the generator emits
 * them under `entry.<fieldKey>.…` and the paths are fixed by construction: the Afkeuren action
 * targets `entry.<fieldKey>.declineMessage` and the Goedkeuren condition reads
 * `entry.<fieldKey>.flagCount`. Nothing here is configurable, which is the point — an
 * admin can no longer point the review at a field the interface actions do not read.
 */
const FLAGS_SUB_KEY = 'flags';
const DECLINE_MESSAGE_SUB_KEY = 'declineMessage';
const FLAG_COUNT_SUB_KEY = 'flagCount';

/**
 * Transient marker written by the backend sync action 4.0 on every carrier submit, so the review
 * can tell a RE-submit from the state it was already showing. It is a leaf write inside this
 * field's own value (the other review keys are untouched by it), and it is consumed here exactly
 * once: the flags become resolved and the marker is cleared with an explicit `null` in the same
 * write. Nothing reads it afterwards — a marker that stayed would resolve the reviewer's NEXT
 * round of flags the moment he typed them.
 *
 * Deliberately NOT declared in `ONBOARDING_REVIEW_FIELDS`: the details-form save path writes back
 * only the sub-controls Formly built from that list, and the entry PATCH deep-merges, so a key
 * that is not in the form is never overwritten by a save. Declaring it would put a stale
 * `resubmitted` value in the saved model instead — and the only writer that must ever clear it is
 * this field's own `#persist`.
 */
const RESUBMITTED_SUB_KEY = 'resubmitted';

/**
 * SIBLING key on the aanmelding holding the link to the onboarding entry — NOT this field's own
 * key, and never written from here.
 *
 * The 4.0/5.0 sync actions write it with `basedOn: 'entry'`, i.e. the WHOLE onboarding document,
 * on every wizard submit and on every re-submit. The review therefore reads it and leaves it
 * alone: a review write that carried the link back would overwrite whatever the sync last wrote, and the
 * review state that used to live inside it would be wiped by the next submit in return. This
 * field's own value (`entry.<fieldKey>`) holds review state only.
 */
const ONBOARDING_LINK_KEY = 'user-onboarding';

const DOCUMENT_MODULE_CODE = 'document-records';
const ONBOARDING_ENTRY_MODULE_CODE = 'onboarding-entry';
const ONBOARDING_FORMS_MODULE_CODE = 'onboarding-forms';

/** Wire page size for reading document records back. `#readDocumentRecords` pages through as
 * many of these as it takes to reach the end, rather than trusting a single page. */
const DOCUMENT_RECORDS_PAGE_SIZE = 50;

interface ReviewFlushWaiter {
  failed: boolean;
  settle: (succeeded: boolean) => void;
}

/**
 * Onboarding overview field — renders three explicit sections inside an
 * onboarding-aanmelding detail-view:
 *
 *   1. **Aanvrager** — applicant identity from the parent aanmelding (read via
 *      `formState.aanmelding`). Dense definition-list, scannable in 2 seconds. NOT flaggable:
 *      name/e-mail/bedrijf/vestiging/formulier/contract/status are all admin-owned on the
 *      aanmelding, so the carrier could never act on a marking here — unlike the wizard pages
 *      and documents below, which the carrier DID fill in and CAN correct.
 *   2. **Onboarding-formulier** — the wizard answers the applicant filled in,
 *      grouped by wizard-page label, EXCLUDING the documenten-page (which is
 *      surfaced separately as section 3).
 *   3. **Documenten** — the wizard's own documenten-page (with its
 *      `required-documents-checklist` field) rendered in readonly form so the
 *      admin sees the same uploads the applicant submitted, with the same
 *      proven loading logic that the public-side wizard already uses.
 *
 * The component still registers under the `readonly_onboarding` field-type
 * key for backwards-compatibility with existing dd-config; only the
 * dropdown-label is renamed to "Onboarding-overzicht".
 *
 * Parent panel must pass `formState.aanmelding` for section 1 to populate.
 */
/**
 * A page or field hidden for technical reasons (flag, `hidden` class, hidden width) — never shown
 * to the applicant. The class is matched as a token (`hasHiddenClass`): a field carrying a utility
 * class like `overflow-hidden` is not hidden.
 */
function isTechnicallyHidden(hide: unknown, className: unknown, width: unknown): boolean {
  return hide === true || hasHiddenClass(className) || width === 'hidden';
}

@Component({
  selector: 'formly-readonly-onboarding',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatIconModule,
    MatProgressSpinnerModule,
    ReviewFlagButtonComponent,
    ReviewFlagPanelComponent,
    LabelListComponent,
    DocumentCardComponent,
    StatusBadgeComponent,
    FuseAlertComponent,
  ],
  template: `
    <!-- Onboarding-review "Edit card" (Figma contract §2: bg-card, rounded-xl, p-7/p-6, no
         shadow). Container→Slot gap 20px (§2), section gap 24px (§3) — the accordion/<details>
         interaction is gone; every section is always open. -->
    <div class="w-full bg-card rounded-xl py-7 px-6 flex flex-col gap-5" data-cy="review-card">
      <div class="flex items-center justify-between gap-3">
        <div class="flex items-center gap-3">
          <span
            class="inline-flex items-center justify-center flex-none w-[1.875rem] h-[1.875rem] rounded-full bg-[var(--dd-badge-accent-bg)]"
            aria-hidden="true">
            <mat-icon
              class="icon-size-4 text-[color:var(--dd-badge-accent-fg)]"
              svgIcon="heroicons_outline:user"></mat-icon>
          </span>
          <span class="text-[0.9375rem] font-medium leading-[1.3] text-default">Onboarding details</span>
        </div>
        <!--
          Figma 4082:162022 ("1 flagged"): one running total for the WHOLE review, sections
          included — the reviewer needs to know that something is still marked even when the
          documents section is scrolled out of view. Per-section counts stay off the headings.
        -->
        @if (flaggingEnabled() && openFlagCount() > 0) {
          <app-status-badge
            data-cy="review-flag-count-badge"
            [label]="openFlagCount() + ' gemarkeerd'"
            variant="warning"
            icon="heroicons_outline:flag" />
        } @else if (flaggingEnabled() && resolvedFlagCount() > 0) {
          <!-- Nothing is open any more, but the round left marks behind: a quiet line rather than
               a badge, so the header reads "clear to approve" at a glance while still saying that
               the carrier reworked something. -->
          <span
            class="text-[0.8125rem] leading-none text-[color:var(--dd-card-muted-fg)]"
            data-cy="review-resolved-count">
            {{ resolvedFlagCount() }} afgehandeld
          </span>
        }
      </div>

      <!--
        Markings whose target is not on this card any more: a wizard step hidden by a condition (or
        left without a single answer), a document that is no longer part of this user's set, and
        legacy targets that never had a control of their own. They keep counting in the flag count,
        which is exactly what hides Goedkeuren — so they are shown with a way out instead of
        blocking the review from somewhere the reviewer cannot see.
      -->
      @if (unreachableFlags().length > 0) {
        <div
          class="flex flex-col gap-2 rounded-lg border border-[color:var(--dd-card-border-strong)] p-4"
          data-cy="review-unreachable-flags">
          <h5 class="m-0 text-[0.8125rem] font-medium leading-none text-default">Onbereikbare markeringen</h5>
          <p class="m-0 text-[0.8125rem] leading-[1.4] text-[color:var(--dd-card-muted-fg)]">
            Deze markeringen horen bij een onderdeel dat hier niet meer getoond wordt. Ze tellen wel mee — verwijder ze
            om de onboarding te kunnen goedkeuren.
          </p>
          <ul class="m-0 flex list-none flex-col gap-2 p-0" role="list">
            @for (flag of unreachableFlags(); track flag.target) {
              <li class="flex items-center justify-between gap-3">
                <span class="text-[0.8125rem] leading-[1.4] text-default">{{ flag.label || flag.target }}</span>
                <button
                  type="button"
                  class="flex-none rounded-lg border border-[color:var(--dd-card-border-strong)] px-3 py-1
                         text-[0.8125rem] leading-[1.4] text-default transition-colors
                         hover:bg-black/[0.08] dark:hover:bg-white/[0.08]"
                  [attr.data-cy]="'review-unreachable-remove-' + flag.target"
                  (click)="removeFlag(flag.target)">
                  Verwijderen
                </button>
              </li>
            }
          </ul>
        </div>
      }

      <div class="flex flex-col gap-6">
        <!-- Section 1: Aanvrager — Label list grid (contract §4), 2 columns / 20px gap / 12px row-gap.
             No flag button: name/e-mail/bedrijf/vestiging/formulier/contract/status are all
             admin-owned on the aanmelding, so the carrier cannot act on a marking here — this
             section renders plain, like any other unflaggable content. -->
        <section class="flex flex-col gap-3" data-cy="onboarding-overview-section-aanvrager">
          <h4 class="text-base font-medium leading-none text-[color:var(--dd-accent)] m-0">Aanvrager</h4>
          @if (applicantFields().length === 0) {
            <p class="text-[0.8125rem] text-accent m-0 italic" data-cy="review-empty">
              Geen aanvrager-gegevens beschikbaar.
            </p>
          } @else {
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-3">
              @for (f of applicantFields(); track f.label) {
                <dd-label-list data-cy="review-row" [label]="f.label" [value]="f.value"></dd-label-list>
              }
            </div>
          }
        </section>

        <!-- Section 2: Onboarding-formulier — wizard answers as Label-list rows (contract §4),
             same grid as the Aanvrager section. Only answered fields render: an empty answer is
             skipped rather than shown as a dead "—" row, so a half-filled form stays compact and
             an untouched form falls through to the empty-state text below. -->
        <section class="flex flex-col gap-3" data-cy="onboarding-overview-section-formulier">
          <h4 class="text-base font-medium leading-none text-[color:var(--dd-accent)] m-0">Onboarding-formulier</h4>
          @if (isLoadingForm()) {
            <div class="flex items-center gap-3 py-2">
              <mat-spinner diameter="20"></mat-spinner>
              <span class="text-[0.8125rem] text-accent">Antwoorden laden…</span>
            </div>
          } @else if (answerSections().length === 0) {
            <p class="text-[0.8125rem] text-accent m-0 italic" data-cy="review-empty">
              De vervoerder heeft het formulier nog niet ingevuld of de gegevens zijn niet meer beschikbaar.
            </p>
          } @else {
            <div class="flex flex-col gap-5">
              @for (section of answerSections(); track $index) {
                <div class="flex flex-col gap-3">
                  @if (section.label) {
                    <div class="flex items-center justify-between gap-2">
                      <h5
                        class="text-[0.75rem] font-medium uppercase tracking-[0.08em] text-[color:var(--dd-accent)] m-0">
                        {{ section.label }}
                      </h5>
                      @if (flaggingEnabled() && !reviewLocked()) {
                        <dd-review-flag-button
                          label="{{ section.label }}"
                          [flagged]="isTargetFlagged(section.target)"
                          [resolved]="isTargetResolved(section.target)"
                          (toggle)="toggleFlag(section.target, section.label, defaultSectionReason)"
                          [dataCy]="'review-flag-' + section.target" />
                      }
                    </div>
                  }
                  <dd-review-flag-panel
                    [flagged]="isTargetFlagged(section.target)"
                    [resolved]="isTargetResolved(section.target)"
                    [reason]="reasonFor(section.target)"
                    [disabled]="reviewLocked()"
                    (reasonChange)="setReason(section.target, $event)"
                    [dataCy]="'review-flag-panel-' + section.target">
                    <div class="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-3">
                      @for (row of section.rows; track $index) {
                        <dd-label-list
                          data-cy="review-row"
                          [label]="row.label"
                          [value]="row.value"
                          [fullWidth]="row.fullWidth"
                          [multiline]="row.fullWidth"></dd-label-list>
                      }
                    </div>
                  </dd-review-flag-panel>
                </div>
              }
            </div>
          }
        </section>

        <!-- Section 3: Documenten — renders the SHARED app-document-card (#21036) instead of a
             hand-rolled card, so this surface stays in lockstep with the checklist and the
             documents-cards entity template, on the 'review' variant: the status badge sits
             inline behind the title (commit a4670477 — the reviewer still needs to see the
             current verdict, e.g. an approved-but-expired or previously-rejected document) and
             the flag toggle is projected into the card's documentCardLabelActions slot, right
             behind the label.
             The per-document Goedkeuren/Afwijzen interface actions are gone: an admin now flags
             the wrong document instead, and the aanmelding's status transition (Task 7) runs the
             document actions programmatically — denied → every flagged document rejected with its
             reason, approved → every remaining (unflagged) document approved. -->
        <section class="flex flex-col gap-3" data-cy="onboarding-overview-section-documenten">
          <h4 class="text-base font-medium leading-none text-[color:var(--dd-accent)] m-0">Documenten &amp; uploads</h4>
          <!--
            Marking is not the decision — a flag only takes effect once the aanmelding itself is
            denied. That sequence is not visible from the buttons, so it is spelled out here.
            Dismissal is session-only on purpose: it costs nothing to re-read after a reload, and
            persisting it would need a user-preference key for a one-line instruction.
          -->
          @if (flaggingEnabled() && !reviewLocked() && documents().length > 0 && !bannerDismissed()) {
            <fuse-alert
              data-cy="review-documents-banner"
              type="info"
              appearance="soft"
              [dismissible]="true"
              (dismissedChanged)="bannerDismissed.set($event)">
              Beoordeel de documenten zorgvuldig. Markeer fouten en keur daarna de onboarding af.
            </fuse-alert>
          }
          @if (documentsLoading()) {
            <div class="flex items-center justify-center gap-3 py-8">
              <mat-spinner diameter="20"></mat-spinner>
              <span class="text-[0.8125rem] text-accent">Documenten laden…</span>
            </div>
          } @else if (documents().length === 0) {
            <p class="text-[0.8125rem] text-accent text-center m-0 italic py-6" data-cy="review-empty">
              Geen documenten geüpload.
            </p>
          } @else {
            <ul class="m-0 flex list-none flex-col gap-4 p-0" role="list">
              @for (doc of documents(); track doc.id) {
                <li>
                  <dd-review-flag-panel
                    [flagged]="isTargetFlagged(doc.id ?? '')"
                    [resolved]="isTargetResolved(doc.id ?? '')"
                    [reason]="reasonFor(doc.id ?? '')"
                    [disabled]="reviewLocked() || !documentFlaggable(doc)"
                    (reasonChange)="setReason(doc.id ?? '', $event)"
                    [dataCy]="'review-flag-panel-' + doc.id">
                    <app-document-card
                      variant="review"
                      data-cy="review-document-card"
                      [density]="documentCardDensity()"
                      [title]="documentTitle(doc)"
                      [badge]="documentBadge(doc)"
                      [reviewComment]="documentReviewComment(doc)"
                      [reviewedBy]="documentReviewedBy(doc)"
                      [reviewedAt]="documentReviewedAt(doc)"
                      [file]="documentCardFile(doc)"
                      [menu]="documentCardMenu(doc)"
                      [meta]="documentMeta(doc)"
                      [dataCy]="'onboarding-overview-doc-' + doc.id"
                      (preview)="previewDocument(doc)"
                      (download)="downloadDocument(doc)"
                      (history)="openDocumentHistory(doc)"
                      (archive)="archiveDocument(doc)">
                      @if (documentFlagToggleVisible(doc)) {
                        <dd-review-flag-button
                          documentCardLabelActions
                          [label]="documentTitle(doc)"
                          [flagged]="isTargetFlagged(doc.id ?? '')"
                          [resolved]="isTargetResolved(doc.id ?? '')"
                          (toggle)="toggleDocumentFlag(doc)"
                          [dataCy]="'review-flag-' + doc.id" />
                      }
                    </app-document-card>
                  </dd-review-flag-panel>
                </li>
              }
            </ul>
          }
        </section>
      </div>
    </div>
  `,
  styles: `
    :host {
      display: block;
      width: 100%;
    }
  `,
})
export class FormlyReadonlyOnboardingComponent extends FieldType implements OnInit {
  readonly #modulesStore = inject(ModulesStore);
  readonly #entriesService = inject(EntriesService);
  readonly #reviewDocumentsQuery = inject(ReviewDocumentsQueryService);
  readonly #checklistTemplates = inject(ReviewChecklistTemplatesService);
  /** Only for `hideConditionalFunction` — the answer rows must honour conditional hides. */
  readonly #formService = inject(FormService);
  readonly #utilityService = inject(UtilityService);
  readonly #documentActions = inject(DocumentFileActionsService);
  readonly #dialog = inject(MatDialog);
  readonly #userStore = inject(UserStore);
  readonly #destroyRef = inject(DestroyRef);
  readonly #reviewWriteRegistry = inject(ReviewWriteRegistry);
  readonly #reviewFlagPersistence = inject(ReviewFlagPersistenceService);
  #unregisterReviewWriter: (() => void) | null = null;

  /**
   * Memoized last-pushed pending-count zodat hetzelfde getal niet bij iedere
   * documents-snapshot opnieuw naar de BE gepatcht wordt (zou anders een
   * patch-loop kunnen geven bij re-render).
   */
  readonly #lastSyncedFlagCount = signal<number | null>(null);
  /** A document count is authoritative only after the current exact-user request succeeded. */
  readonly #documentsLoadSucceeded = signal(false);
  /** Invalidates callbacks from a request superseded by a later aanmelding/user binding. */
  #documentsRequestGeneration = 0;
  /**
   * Aanmelding id of the last `#refreshDocuments` run; `undefined` = never ran. Distinguishes a
   * genuine aanmelding switch from a same-id re-bind (see `#refreshDocuments`).
   */
  #lastRefreshedAanmeldingId: string | null | undefined = undefined;

  /**
   * Aanmelding whose onboarding entry is already resolved; `undefined` = nothing resolved yet.
   * Guards `#resolveOnboardingLink` against re-running per `valueChanges` emission.
   */
  #resolvedForAanmeldingId: string | null | undefined = undefined;

  constructor() {
    super();
    // Diagnostic — track what the store actually holds for documenten so
    // we can see whether loadEntries populated it or it stays empty.
    effect(() => {
      const moduleId = this.documentModuleId();
      if (!moduleId) return;
      const all = this.#documentRecords();
      debugLog('OnboardingOverview', 'store.documenten.snapshot', {
        moduleId,
        storedCount: all.length,
        storedIds: all.map(e => e.id).slice(0, 10),
        scopeUserId: this.#docsScopeUserId(),
        afterFilter: this.documents().length,
      });
    });

    // Keep `flagCount` aligned with the persisted flag list. The Goedkeuren action's condition
    // (`entry.<fieldKey>.flagCount == 0`) is config; what blocks approval is any OPEN MARKING —
    // a flagged document, wizard page or applicant section alike. Unflagged pending documents are
    // approved by the root-owned ReviewDecisionCoordinator once the parent action settles in the
    // shared action pipeline (EntriesService.processAction) — on the detail page and in the review
    // inbox alike. This field does not fan out itself.
    //
    // Reads the PERSISTED flags (readFlags(aanmelding)), deliberately not `this.flags()`: this
    // effect corrects drift between the store and the persisted flag list, and must stay quiet
    // while an optimistic edit is only sitting in `#localFlags` — otherwise it races the debounced
    // `#schedulePersist` write, and its own `#persist` call resets `#localFlags` mid-edit before
    // the composed flags/decline-message ever reach the server (measured: 3 PATCHes instead of 1,
    // the last one carrying an emptied flag list).
    effect(() => {
      const aanmelding = this.aanmelding();
      if (!aanmelding?.id || !aanmelding.moduleId) return;
      // Flagging switched off makes the review display-only: it writes nothing at all, so it must
      // not maintain a count either — the module keeps whatever value it already has.
      if (!this.flaggingEnabled()) return;
      // Asked BEFORE anything is recorded: the refusal handling at the bottom puts
      // `#lastSyncedFlagCount` back to null so a correction that never left is not remembered as
      // done — which against a key `#persist` refuses EVERY time is an effect that re-runs itself
      // forever (measured: the Karma browser stopped answering on the misconfigured-key spec).
      if (!this.#canOwnValue()) return;
      // Only a request that is still UNDER REVIEW is corrected. `flagCount` exists to gate the
      // Goedkeuren action, which a decided request does not offer any more — while writing it is a
      // real entry change that re-fires the module's own status actions (the carrier's
      // afkeur-mail among them) for a value nothing reads. Opening an old afgekeurd request to
      // read it back must stay a read.
      if (this.reviewLocked()) return;
      if (!this.#documentsLoadSucceeded()) return;
      // OPEN flags only — a resolved flag (the carrier resubmitted on it) must not keep Goedkeuren
      // blocked, which is exactly what this count gates.
      const flagCount = openFlags(readFlags(aanmelding, this.#fieldKey())).length;
      if (this.#lastSyncedFlagCount() === flagCount) return;
      const storedCount = this.#storedOwnValue(aanmelding)[FLAG_COUNT_SUB_KEY];
      // An ABSENT count does NOT answer the Goedkeuren condition the way a stored zero does:
      // `matchesEntryConditions` returns false the moment the compare value is null/undefined
      // (interface-actions.visibility.ts), so `entry.<fieldKey>.flagCount == 0` fails CLOSED and
      // the button never renders on a request whose count was never written. Seeding it is why
      // this effect is gated on `reviewLocked()` above: under review is the only status where the
      // gate is read, and the only one where an entryUpdated starts no aanmelding action.
      if (storedCount === flagCount) {
        this.#lastSyncedFlagCount.set(flagCount);
        return;
      }
      // The count and NOTHING else. The persistence layer rebases it onto the current server row,
      // so flags and composed decline text keep whatever the server has: re-sending them would let this
      // correction overwrite the admin's own deny text with an older composed version, and would
      // race a debounced flag write that is still in flight with a list that predates it.
      //
      // Recorded BEFORE the write leaves, so this effect cannot send a second correction while the
      // first is in flight (it re-runs on the optimistic store write `#persist` makes). A failure
      // re-arms it once, through `#settleCountSync`.
      this.#lastSyncedFlagCount.set(flagCount);
      const sent = untracked(() =>
        this.#persist({ [FLAG_COUNT_SUB_KEY]: flagCount }, aanmelding, {
          clearsLocalFlags: false,
          onSettled: succeeded => this.#settleCountSync(succeeded),
        })
      );
      // `#persist` refuses a misconfigured field key outright; memoizing a count it never carried
      // would make this effect go quiet about a correction that never happened.
      if (!sent) this.#lastSyncedFlagCount.set(null);
    });

    // A carrier re-submit RESOLVES the open flags (see RESUBMITTED_SUB_KEY). Without this, the
    // reviewer had to clear every marking by hand before Goedkeuren appeared again — its condition
    // (`entry.<fieldKey>.flagCount == 0`) reads the count this writes. The flags stay in the list,
    // muted and with their reason, so the next round starts from what was asked last time.
    //
    // Runs at most once per marker: `#resubmitResolved` blocks a re-entry while the PATCH is in
    // flight (the effect re-runs on every store write to this aanmelding), and resets itself the
    // moment the store shows the marker cleared — a LATER re-submit is a new marker and resolves
    // again. Refused writes (`#persist` returns false on a misconfigured field key) leave the flag
    // down, so the next marker is not silently swallowed.
    effect(() => {
      const aanmelding = this.aanmelding();
      if (!aanmelding?.id || !aanmelding.moduleId) return;
      // Display-only reviews write nothing at all, marker or not.
      if (!this.flaggingEnabled()) return;
      const ownValue = this.#storedOwnValue(aanmelding);
      if (ownValue[RESUBMITTED_SUB_KEY] !== true) {
        this.#resubmitResolved = false;
        return;
      }
      if (this.#resubmitResolved) return;

      const persisted = readFlags(aanmelding, this.#fieldKey());
      // An optimistic edit — and the debounce that is about to write it — predates this resolve,
      // and both edits touch the same list: letting both leave independently could reopen every
      // flag this resolve just closed. Fold the two lists
      // into one resolved list and drop the pending write, so ONE list reaches the server.
      const local = untracked(() => this.#localFlags());
      const flags = local === null ? persisted : this.#mergeFlagLists(persisted, local);
      const resolved = resolveFlags(flags);
      if (local !== null) {
        this.#cancelScheduledPersist();
        this.#localFlags.set(resolved);
      }
      // Clearing the marker with an explicit null is the whole write when there is nothing to
      // resolve — leaving it set would resolve the reviewer's next round on sight.
      const ownWrite = flags.length
        ? { [FLAGS_SUB_KEY]: resolved, [FLAG_COUNT_SUB_KEY]: 0, [RESUBMITTED_SUB_KEY]: null }
        : { [RESUBMITTED_SUB_KEY]: null };
      // Raised BEFORE the write leaves: `#persist` mirrors the cleared marker into the store
      // immediately, which re-runs this effect, and a marker still being resolved must not start a
      // second round. `#settleResubmitResolve` lowers it again when the write fails.
      this.#resubmitResolved = true;
      const sent = untracked(() =>
        this.#persist(ownWrite, aanmelding, {
          clearsLocalFlags: local !== null,
          onSettled: succeeded => this.#settleResubmitResolve(succeeded),
          // Carries RESUBMITTED_SUB_KEY, which no later ordinary flag write repeats — dropping this
          // one for a newer flag write would silently strand the marker on the server.
          replaceable: false,
        })
      );
      if (!sent) {
        // Refused (misconfigured field key) — leave the flag down so the next marker is not
        // silently swallowed.
        this.#resubmitResolved = false;
        return;
      }
      // The count this just wrote, remembered so the count-sync effect above does not send a
      // second PATCH for the same zero.
      if (flags.length) this.#lastSyncedFlagCount.set(0);
    });

    // Registered here rather than in `ngOnInit`: a component that is torn down before Formly wires
    // it still must not leave a timer running.
    this.#destroyRef.onDestroy(() => this.#onDestroy());
  }

  /**
   * Nothing this component started may outlive it. A flag edit still sitting in the debounce is a
   * marking the reviewer believes he made — dropping it silently is the one outcome he cannot
   * detect.
   *
   * A review write that already left is the exception and needs nothing here: `#sendWrite` does not
   * cancel on destroy, so the request in flight lands and drains whatever waited behind it.
   *
   * The flush goes out through `#persistFlags` — the same path the debounce itself would have
   * taken — and not straight onto the wire. Sending it directly beside a write already in flight
   * lets two writes to the same sub-keys land in whatever order the
   * server answers them: the older one arriving last wrote its own full flag list back over the
   * newer one. Closing the panel is precisely when nobody is watching, so that lost marking is
   * undetectable. Queued, it waits for the write ahead of it and `#settleWrite` sends it after.
   */
  #onDestroy(): void {
    this.#unregisterReviewWriter?.();
    this.#unregisterReviewWriter = null;
    if (this.#persistTimer) {
      this.#cancelScheduledPersist();
      this.#persistFlags();
    }
  }

  /**
   * Mirror what was just persisted into this field's own control, so an explicit detail-page save
   * cannot write the pre-review values back over it.
   *
   * The field owns sub-fields now, so Formly builds a FormGroup for it and each review value has
   * its own nested FormControl. Every write goes to those sub-controls with `{ emitEvent: false }`:
   * emitting would re-enter this component's own `valueChanges` subscription (set up in
   * `ngOnInit`) and reload the document records on every single flag click.
   *
   * That silence is safe: `updateEntryState` changes the store entry, and `DetailsService.model`
   * is rebuilt from it (Formly then re-applies it to this field) only while the form holds no other
   * edits to submit; while it does, the model is held and not rebuilt, so this write is what keeps
   * the control current. `DetailsService.formValue` is
   * `toSignal(form().valueChanges.pipe(map(() => form().getRawValue())))`, so it only refreshes on
   * an emission — this write keeps `getRawValue()` (what an explicit save reads) consistent with
   * what was just persisted.
   *
   * Anything that is NOT a plain FormControl is skipped. `setValue` on a FormArray asserts
   * control/value length parity and THROWS (NG01000/1/2), and one throw inside this loop used to
   * take every remaining write — and the optimistic-flag reset behind them — down with it.
   * Programmatic `setValue` emits the model update but does not mark the control or form dirty:
   * these are not user edits, and marking them would arm the save button and UnSavedChangesGuard.
   */
  #syncOwnValueToDetailsForm(aanmelding: Entry, ownValue: Record<string, unknown>): void {
    const detailsEntry = this.#detailsService?.entry();
    if (detailsEntry?.id !== aanmelding.id || detailsEntry.moduleId !== aanmelding.moduleId) return;

    const form = this.#detailsService?.form();
    if (!form) return;
    const control = (this.formControl ?? form.get(this.#fieldKey())) as unknown;

    // Without the sub-fields (a legacy generatedForm that has not been regenerated yet) the
    // control is still a plain FormControl holding the whole object — write it as one value.
    if (control instanceof FormControl) {
      if (control.value !== ownValue) control.setValue(ownValue, { emitEvent: false });
      return;
    }
    if (!(control instanceof FormGroup)) return;

    for (const [key, value] of Object.entries(ownValue)) {
      const subControl = control.get(key) as unknown;
      if (!(subControl instanceof FormControl) || subControl.value === value) continue;
      subControl.setValue(value, { emitEvent: false });
    }
  }

  /**
   * This field's own stored value on the aanmelding: the object holding the review sub-keys, plus
   * whatever onboarding-entry reference was stored alongside them. Anything that is not a plain
   * object (a thin string reference, a missing value) reads as empty.
   */
  #storedOwnValue(aanmelding: Entry | null): Record<string, unknown> {
    const value = aanmelding?.entry?.[this.#fieldKey()];
    return value && typeof value === 'object' && !Array.isArray(value) ? { ...(value as Record<string, unknown>) } : {};
  }

  /**
   * An onboarding-entry link, or null. Only an `id` makes a value a link: this field's own value
   * is the review state (`{ flags, declineMessage, flagCount }`), and that object must never be
   * mistaken for an onboarding entry — it would render section 2 as an answered-but-empty form
   * and make `#resolveOnboardingEntry` skip its reverse lookup on the next re-bind.
   */
  #asOnboardingLink(value: unknown): Entry | null {
    return value && typeof value === 'object' && (value as Entry).id ? (value as Entry) : null;
  }

  /* ── Review flags ─────────────────────────────────────────────────────── */

  /**
   * Session-only dismissal of the documents instruction banner. Deliberately not persisted: the
   * line is one sentence, a reload costs nothing, and a user-preference key for it would outlive
   * the release in which the instruction is still new.
   */
  readonly bannerDismissed = signal(false);

  /** Unsaved edits win over the persisted list until the ETag-aware save settles (optimistic UI). */
  readonly #localFlags = signal<ReviewFlag[] | null>(null);
  #persistTimer: ReturnType<typeof setTimeout> | null = null;
  /**
   * Whether the resubmit marker currently in the store has already been acted on. A plain field,
   * not a signal: it is bookkeeping FOR an effect, and making it reactive would re-run that effect
   * on its own write. See the resubmit effect for how it resets.
   */
  #resubmitResolved = false;
  /**
   * Whether the CURRENT resubmit marker has already cost a failed write. A failed resolve re-arms
   * the effect so the marker is not silently swallowed — but exactly once: the rollback puts the
   * marker back and re-runs the effect on its own, so an unbounded re-arm turns a server that
   * keeps refusing into a PATCH loop. Reset on the first write that succeeds.
   */
  #resubmitResolveRetried = false;
  /** Same one-retry bound for the count correction — see `#settleCountSync`. */
  #countSyncRetried = false;
  /** See `#persist`: the link-key refusal is reported once per component, not once per write. */
  #linkKeyRefused = false;
  /**
   * One review write on the wire at a time — see `PendingReviewWrite`. Plain fields, not signals:
   * they are `#persist`'s own bookkeeping, and making them reactive would re-run the very effects
   * that write through them. An idle queue sends synchronously, so the common single-write path is
   * unchanged; only genuinely overlapping writes wait.
   */
  #writeInFlight = false;
  readonly #writeQueue: PendingReviewWrite[] = [];
  /** The flag write currently WAITING (never the one on the wire) — the one a newer one replaces. */
  #queuedFlagWrite: PendingReviewWrite | null = null;
  readonly #flushWaiters = new Set<ReviewFlushWaiter>();

  /**
   * Configuration seeded from this field's own props in `ngOnInit` — the same snapshot pattern the
   * aanmelding/onboarding-value signals use, and for the same reason: `field` is a plain Formly
   * property, so a `computed` reading it would cache whatever it saw on its first evaluation.
   *
   * `#fieldKey` is this field's Formly key (`review` in the current config). All three review
   * values live INSIDE that field's own entry value, under the sub-keys above, which is what
   * makes the feature self-contained: the aanmelding module carries no flags, afkeurtekst or
   * telveld field. It is NEVER the onboarding link — that is ONBOARDING_LINK_KEY.
   */
  readonly #fieldKey = signal<string>('');
  readonly flaggingEnabled = signal<boolean>(true);

  /** Template constant: the prefilled reason a freshly marked section starts with. */
  readonly defaultSectionReason = DEFAULT_SECTION_REASON;

  /** With flagging switched off the review is display-only: even legacy flags stay unread. */
  readonly flags = computed<ReviewFlag[]>(() =>
    this.flaggingEnabled() ? (this.#localFlags() ?? readFlags(this.aanmelding(), this.#fieldKey())) : []
  );

  /** Flags are only meaningful while the request is under review. */
  readonly reviewLocked = computed<boolean>(
    () => this.aanmelding()?.entry?.['status']?.slug !== ONBOARDING_STATUS_SLUGS.inReview
  );

  /** The markings that still ask the carrier for something — the header badge and the count. */
  readonly openFlagCount = computed<number>(() => openFlags(this.flags()).length);
  /** Flags the carrier already resubmitted on: shown muted, summarised as "N afgehandeld". */
  readonly resolvedFlagCount = computed<number>(() => this.flags().length - this.openFlagCount());

  isTargetFlagged(target: string): boolean {
    return isFlagged(this.flags(), target);
  }

  isTargetResolved(target: string): boolean {
    return isFlagResolved(this.flags(), target);
  }

  reasonFor(target: string): string {
    return flagReason(this.flags(), target);
  }

  /**
   * A PENDING, APPROVED or EXPIRED document can be flagged. The document module's Afwijzen action
   * fires on status ∈ {pending, approved, expired}, so an already approved record — e.g. from an
   * earlier review round — or one that has since expired can still be sent back for correction:
   * an expired record is still an admin decision, and the carrier only replaces it after the deny
   * transition. REJECTED documents are resolved by the carrier re-uploading instead: flagging one
   * would let `executeWithValues` report the action as not visible and hand the admin nothing but
   * a failure toast, so the flag is refused up front for that status.
   */
  documentFlaggable(doc: Entry): boolean {
    if (!this.flaggingEnabled() || this.reviewLocked()) return false;
    const slug = this.#documentStatusSlug(doc);
    return slug === 'pending' || slug === 'approved' || slug === 'expired';
  }

  /**
   * Whether the document's flag button renders at all. A document that can no longer be FLAGGED
   * (a rejected one) still shows the button while it carries an OPEN marking: that marking counts
   * in `flagCount`, which is exactly what hides Goedkeuren, and with no control on screen the
   * review could never be unblocked again. A resolved marking needs no button — it counts for
   * nothing and blocks nothing.
   */
  documentFlagToggleVisible(doc: Entry): boolean {
    if (!this.flaggingEnabled() || this.reviewLocked()) return false;
    return this.documentFlaggable(doc) || this.isTargetFlagged(doc.id ?? '');
  }

  /**
   * One click on a document's flag button. A document that is no longer flaggable only offers
   * REMOVAL — re-raising a marking there would ask for something the deny transition cannot
   * deliver (the Afwijzen action does not fire on a rejected record), while leaving it in place
   * keeps the request unapprovable.
   */
  toggleDocumentFlag(doc: Entry): void {
    const target = doc.id ?? '';
    if (!this.documentFlaggable(doc)) {
      this.removeFlag(target);
      return;
    }
    this.toggleFlag(target, this.documentTitle(doc), this.defaultDocumentReason(doc));
  }

  /**
   * `initialReason` is what a NEW flag starts with: the template passes the section default or
   * the status-aware document default, so the carrier always reads an instruction instead of a
   * bare label. Removing or reopening a flag ignores it (see `toggleFlag` in the util).
   */
  toggleFlag(target: string, label: string, initialReason = ''): void {
    if (!this.flaggingEnabled() || this.reviewLocked()) return;
    this.#localFlags.set(toggleFlag(this.flags(), target, label, initialReason));
    this.#schedulePersist();
  }

  /**
   * Drop a marking outright, without the "reopen a resolved one" step `toggleFlag` offers. It is
   * the only action left on a target the reviewer cannot reach any more (see `unreachableFlags`)
   * or may not raise again (a document that has since been rejected).
   */
  removeFlag(target: string): void {
    if (!this.flaggingEnabled() || this.reviewLocked()) return;
    const flags = this.flags();
    if (!flags.some(flag => flag.target === target)) return;
    this.#localFlags.set(flags.filter(flag => flag.target !== target));
    this.#schedulePersist();
  }

  /**
   * Markings the reviewer can no longer reach from this card: a wizard step hidden by a condition
   * (or left without a single answer), a document that is not in this user's set any more, and
   * legacy targets that never had a control of their own. They still count in `flagCount`, so
   * without this block the review stays unapprovable with nothing on screen to explain it.
   *
   * Resolved markings are left out — they count for nothing. Nothing is judged unreachable before
   * both sources have actually resolved: every document flag looks orphaned while the records are
   * still loading, and every page flag does while the form is still being fetched.
   */
  readonly unreachableFlags = computed<ReviewFlag[]>(() => {
    if (!this.flaggingEnabled() || this.reviewLocked()) return [];
    if (!this.#documentsLoadSucceeded() || this.isLoadingForm()) return [];
    const reachable = new Set<string>([
      ...this.answerSections().map(section => section.target),
      ...this.documents().map(doc => doc.id ?? ''),
    ]);
    return openFlags(this.flags()).filter(flag => !reachable.has(flag.target));
  });

  /**
   * Union of two flag lists by target, with `overrides` winning on a shared target. Both sides can
   * carry a marking the other has never seen — the persisted list comes back from the carrier's
   * re-submit, the local one is the reviewer's unsaved click. Both must enter the queued operation.
   * A removal that exists only locally is
   * deliberately NOT honoured: keeping a marking the reviewer clears again with one click beats
   * dropping one he will never see.
   */
  #mergeFlagLists(base: readonly ReviewFlag[], overrides: readonly ReviewFlag[]): ReviewFlag[] {
    return [
      ...base.map(flag => overrides.find(override => override.target === flag.target) ?? flag),
      ...overrides.filter(override => !base.some(flag => flag.target === override.target)),
    ];
  }

  /**
   * Prefilled reason for marking this document, phrased for the state the reviewer is LOOKING at.
   *
   * Derived through the shared badge, not the raw status slug: an approved record whose einddatum
   * has passed reads "Verlopen" on its own card, and asking for a *complete* document there
   * instead of a current one instructs the carrier to do the wrong thing. Flaggability keeps using
   * the raw slug — that mirrors the document module's Afwijzen condition, which is about the
   * persisted status.
   */
  defaultDocumentReason(doc: Entry): string {
    const badge = this.documentBadge(doc).badge;
    return defaultDocumentReason(badge === 'expired' ? 'expired' : this.#documentStatusSlug(doc));
  }

  /**
   * A reason is persisted IMMEDIATELY, not debounced. The deny popup reads `decline-message` off
   * the store entry synchronously when it opens, so a reason typed right before clicking Afkeuren
   * would miss the prefill if it were still sitting in the 300 ms window. Any pending debounce
   * (from the flag toggle that opened this panel) is cancelled first, so the flag edit and the
   * reason still reach the server as ONE write. Only `toggleFlag` stays debounced — it fires per
   * click, not per keystroke.
   */
  setReason(target: string, reason: string): void {
    if (!this.flaggingEnabled() || this.reviewLocked()) return;
    this.#localFlags.set(setFlagReason(this.flags(), target, reason));
    this.#cancelScheduledPersist();
    this.#persistFlags();
  }

  /**
   * Whether this field can own a value at all: a key-less field has nowhere to write, and
   * ONBOARDING_LINK_KEY belongs to the sync actions, which rewrite it on every submit. `#persist`
   * refuses both — loudly, once per component — and a caller that keeps re-arming on refusal has
   * to know beforehand that the refusal is permanent.
   */
  #canOwnValue(): boolean {
    const fieldKey = this.#fieldKey();
    return !!fieldKey && fieldKey !== ONBOARDING_LINK_KEY;
  }

  #cancelScheduledPersist(): void {
    if (this.#persistTimer) clearTimeout(this.#persistTimer);
    this.#persistTimer = null;
  }

  #schedulePersist(): void {
    this.#cancelScheduledPersist();
    this.#persistTimer = setTimeout(() => {
      this.#persistTimer = null;
      this.#persistFlags();
    }, 300);
  }

  #persistFlags(): boolean {
    const aanmelding = this.aanmelding();
    if (!aanmelding?.id || !aanmelding.moduleId) {
      // No target to write to (e.g. the aanmelding was cleared mid-debounce) — drop the
      // optimistic edit instead of leaving it permanently unsaved and stuck in front of
      // whatever aanmelding binds next.
      this.#localFlags.set(null);
      return false;
    }
    const fieldKey = this.#fieldKey();
    if (!fieldKey) {
      // Nothing to write the flags into — a review field without a Formly key cannot own a value.
      debugLog('OnboardingOverview', 'flags.persist.skipped', 'field has no key');
      this.#localFlags.set(null);
      return false;
    }
    const flags = this.flags();
    // OPEN flags only: a resolved flag is history the reviewer still sees, and counting it would
    // re-block Goedkeuren after a re-submit.
    const flagCount = openFlags(flags).length;
    const sent = this.#persist(this.#flagWriteBody(flags), aanmelding, {
      clearsLocalFlags: true,
      // Every write from here is an interchangeable full snapshot of the same flag list — only the
      // newest one waiting needs to reach the server, so a newer one may drop it outright.
      replaceable: true,
    });
    // Remembered only when the write actually went out — see the count-sync effect: a count
    // recorded for a refused write would silence the correction that is supposed to follow it.
    if (sent) {
      this.#lastSyncedFlagCount.set(flagCount);
    }
    return sent;
  }

  /** Flushes a pending debounce and resolves only after every accepted review write has settled. */
  flushPendingReview(): Observable<boolean> {
    return new Observable<boolean>(subscriber => {
      const waiter: ReviewFlushWaiter = {
        failed: false,
        settle: succeeded => {
          subscriber.next(succeeded);
          subscriber.complete();
        },
      };
      this.#flushWaiters.add(waiter);

      if (this.#persistTimer) {
        this.#cancelScheduledPersist();
        if (!this.#persistFlags()) waiter.failed = true;
      }
      this.#completeFlushWaitersIfIdle();
      return () => this.#flushWaiters.delete(waiter);
    });
  }

  /**
   * This field's own value for a flag write. All three review values live inside it, so the whole
   * review is ONE write — and ONLY that value: the onboarding link is the sibling key's business,
   * and a review save that re-sent it would overwrite whatever the sync actions last wrote there.
   *
   * Shared with the destroy-time flush so a write that leaves as the page closes carries exactly
   * the same body as one that leaves while it is open.
   */
  #flagWriteBody(flags: readonly ReviewFlag[]): Record<string, unknown> {
    return {
      [FLAGS_SUB_KEY]: flags,
      // The composed text is the deny popup's prefill, and this is its single writer in the
      // portal: it is composed from the flags here, never read back out of the (possibly stale)
      // store value. Once the request is no longer under review the admin's final wording must
      // not be overwritten by a late reason edit.
      ...(this.reviewLocked() ? {} : { [DECLINE_MESSAGE_SUB_KEY]: composeDeclineMessage(flags) }),
      [FLAG_COUNT_SUB_KEY]: openFlags(flags).length,
    };
  }

  /**
   * Bookkeeping for the count correction: a failed write must not stay recorded as done, or the
   * stored count keeps its stale value for the rest of the session. Re-armed exactly once — see
   * `#countSyncRetried`.
   */
  #settleCountSync(succeeded: boolean): void {
    if (succeeded) {
      this.#countSyncRetried = false;
      return;
    }
    if (this.#countSyncRetried) return;
    this.#countSyncRetried = true;
    this.#lastSyncedFlagCount.set(null);
  }

  /**
   * Bookkeeping for the resubmit resolution. The marker is only handled once the server confirms
   * it: a write recorded as done on DISPATCH swallowed the marker whenever it failed, leaving the
   * reviewer to clear the whole round by hand. Re-armed exactly once — see
   * `#resubmitResolveRetried`.
   */
  #settleResubmitResolve(succeeded: boolean): void {
    if (succeeded) {
      this.#resubmitResolveRetried = false;
      return;
    }
    this.#resubmitResolved = this.#resubmitResolveRetried;
    this.#resubmitResolveRetried = true;
  }

  /**
   * One ETag-aware, freshly rebased PUT for every review write. Carries the etag the server hands
   * back into the store (Cosmos bumps it per write; a stale etag makes the next action come back 412).
   *
   * Returns whether the write was ACCEPTED: a misconfigured field key is refused outright, and a
   * caller keeping its own "already written" bookkeeping must not record what never left. An
   * accepted write goes out at once when the wire is free and waits its turn otherwise — see
   * `#sendWrite`, which is what keeps two writes to the same sub-key in the order they were made.
   *
   * `clearsLocalFlags` distinguishes the two callers: `#schedulePersist`'s flag-write is the only
   * one allowed to clear the optimistic `#localFlags` override (its own edit has now reached the
   * server) or to show the flag-specific failure text. The count-sync effect's PATCH carries no
   * flag edit of its own — if it also cleared `#localFlags`, it would silently discard whatever
   * flag edit is still sitting there waiting for `#schedulePersist`'s debounce to fire (measured:
   * the debounced write then read the just-cleared, empty `flags()` and persisted `flags: []`).
   */
  #persist(
    ownValue: Record<string, unknown>,
    aanmelding: Entry,
    {
      clearsLocalFlags,
      onSettled,
      replaceable = false,
    }: { clearsLocalFlags: boolean; onSettled?: (succeeded: boolean) => void; replaceable?: boolean }
  ): boolean {
    const fieldKey = this.#fieldKey();
    if (!fieldKey) {
      // A review field without a Formly key cannot own a value — writing under an empty key would
      // create a junk property on the aanmelding instead.
      debugLog('OnboardingOverview', 'review.persist.skipped', 'field has no key');
      return false;
    }
    if (fieldKey === ONBOARDING_LINK_KEY) {
      // An unmigrated tenant still keys this field after the onboarding link. The sync actions own
      // that key and rewrite it on every submit, so a review written there is silently lost —
      // flags, decline text and count together. Refuse the write and say so loudly instead: a
      // console.error is visible without the debug flag, which a data-losing misconfiguration has
      // to be. Once per component, so it stays readable while the reviewer keeps typing.
      if (!this.#linkKeyRefused) {
        this.#linkKeyRefused = true;
        console.error(
          `Review field is keyed as '${ONBOARDING_LINK_KEY}', which is the onboarding link, not the review. ` +
            'Flags are NOT saved until this field gets a key of its own.'
        );
      }
      return false;
    }
    const moduleId = aanmelding.moduleId;
    const entryId = aanmelding.id;
    const write: PendingReviewWrite = {
      moduleId,
      entryId,
      fieldKey,
      aanmelding,
      ownValue,
      // What the CURRENT row holds for the keys this write touches, so a failure can put them back.
      previous: this.#ownValueSlice(this.#storeRow(moduleId, entryId, aanmelding), Object.keys(ownValue)),
      clearsLocalFlags,
      onSettled,
      queued: false,
      superseded: false,
      replaceable,
    };
    // Mirrored into the store BEFORE the request leaves — waiting for its turn included. The deny
    // popup composes its text from the store entry the moment it opens, and an admin can click
    // Afkeuren the instant after typing a reason; waiting denied with the previous round's text.
    this.#writeOwnValueToStore(moduleId, entryId, ownValue, aanmelding);

    if (this.#writeInFlight) {
      if (clearsLocalFlags) {
        // A flag write carries the whole local snapshot, so a
        // newer one is always a fresher snapshot of the same thing: whether the waiting one may be
        // dropped in favour of this one is the WAITING write's own call, made by its caller at the
        // moment it was created (see `replaceable` on `PendingReviewWrite`) — not something this
        // code infers from comparing the two writes' key sets. A key-set comparison cannot tell
        // "this write's extra key is optional context" apart from "this write's extra key is the
        // one thing it exists to send" — which is exactly how the resubmit-resolve write's
        // `RESUBMITTED_SUB_KEY` used to get silently dropped by an ordinary flag write that simply
        // didn't happen to carry every key it did.
        const waiting = this.#queuedFlagWrite;
        if (waiting?.replaceable) waiting.superseded = true;
        this.#queuedFlagWrite = write;
      }
      write.queued = true;
      this.#writeQueue.push(write);
      return true;
    }
    this.#sendWrite(write);
    return true;
  }

  /**
   * Put one accepted write on the wire, and hand the queue on when it settles.
   *
   * The request is deliberately NOT piped through `takeUntilDestroyed`: `#onDestroy` flushes the
   * debounced flag write, but a `#persist` that has already left (the count correction, the
   * resubmit resolution) was cancelled mid-flight by that operator — silently, with its retry
   * bookkeeping never running. Both response handlers only touch the app-wide store and services,
   * which outlive this component, so letting the write land is both safe and the only honest
   * outcome for a review the server has already been told about.
   */
  #sendWrite(write: PendingReviewWrite): void {
    const { moduleId, entryId, fieldKey, aanmelding, ownValue, clearsLocalFlags, onSettled } = write;
    if (this.#queuedFlagWrite === write) this.#queuedFlagWrite = null;
    // A write that waited re-mirrors its own optimistic value onto the row right before it goes
    // out: a failure ahead of it in the queue rolled ITS OWN keys back (see the error handler
    // below), which — for any key the two writes share — clobbers whatever this write already
    // mirrored at enqueue time. `write.previous` needs no recomputation here any more: it is kept
    // current the whole time this write waits, by `#propagateSettledKeys` in every settle below.
    if (write.queued) this.#writeOwnValueToStore(moduleId, entryId, ownValue, aanmelding);
    const previous = write.previous;
    this.#writeInFlight = true;

    this.#reviewFlagPersistence
      .save({
        moduleId,
        entryId,
        fieldKey,
        baseOwnValue: previous,
        desiredOwnValue: ownValue,
      })
      .subscribe({
        next: saved => {
          const persistedOwnValue = saved.ownValue;
          const freshEtag = saved.entry?._etag ?? saved.entry?.etag;
          // Merged onto the row as it stands NOW, never onto the snapshot this write started from:
          // `updateEntryState` REPLACES the row, so a status write (Afkeuren, SignalR, the backend
          // sync) that landed while this save was in flight would be rolled back — unlocking the
          // review and re-running the whole transition on the next change.
          const updated = this.#writeOwnValueToStore(moduleId, entryId, persistedOwnValue, aanmelding, freshEtag);
          this.#syncOwnValueToDetailsForm(updated, persistedOwnValue);
          this.#propagateSettledKeys(Object.keys(ownValue), updated);
          // Only hand control back to the persisted list when nothing newer is waiting: with the
          // immediate `setReason` write, an admin can toggle the next flag while this save is
          // still in flight. Clearing here would make that flag vanish from the UI and let the
          // pending debounce — or the queued write — persist the list without it.
          if (clearsLocalFlags && !this.#persistTimer && !this.#queuedFlagWrite) this.#localFlags.set(null);
          onSettled?.(true);
          this.#settleWrite();
        },
        error: err => {
          // Put back what this write changed, and only that: everything else on the row is
          // whatever landed while the request was out.
          const updated = this.#writeOwnValueToStore(moduleId, entryId, previous, aanmelding);
          this.#propagateSettledKeys(Object.keys(ownValue), updated);
          if (clearsLocalFlags) {
            console.error('Review-markering opslaan vanuit readonly-onboarding mislukt:', err);
            this.#localFlags.set(null);
            this.#utilityService.notify('Markering opslaan mislukt — probeer opnieuw.');
          } else {
            // Both non-flag writers land here: the count-sync correction and the resubmit
            // resolution. Neither is a document write any more, so the text names the review.
            console.error('Review-status bijwerken vanuit readonly-onboarding mislukt:', err);
            this.#utilityService.notify('Review bijwerken mislukt — probeer opnieuw.');
          }
          onSettled?.(false);
          for (const waiter of this.#flushWaiters) waiter.failed = true;
          this.#settleWrite();
        },
      });
  }

  /**
   * Propagate a just-settled write's effect on the row onto every write still waiting behind it.
   *
   * A queued write's `previous` is its rollback baseline — what the row should revert to if IT
   * later fails. That baseline is only correct while nothing else has touched the keys it was
   * snapshotted from; once the write ahead of it in the queue settles (lands, or rolls its own
   * keys back on failure), the row's value for any SHARED key has changed out from under it, and
   * the queued write's `previous` for that key must change with it — read straight off the row
   * `settledOwnValue` was just written to, not guessed by comparing values. A key the settling
   * write did not touch keeps whatever `previous` it already had: nothing changed it, so its
   * enqueue-time snapshot is still exactly right.
   */
  #propagateSettledKeys(settledKeys: readonly string[], row: Entry): void {
    if (this.#writeQueue.length === 0) return;
    const nowOwnValue = this.#storedOwnValue(row);
    for (const queuedWrite of this.#writeQueue) {
      for (const key of settledKeys) {
        if (key in queuedWrite.ownValue) queuedWrite.previous[key] = nowOwnValue[key];
      }
    }
  }

  /**
   * Release the wire and start the next waiting write.
   *
   * `onSettled` runs BEFORE this, while the slot still counts as occupied: its bookkeeping re-runs
   * the count-sync and resubmit effects, and a write they start there has to join the back of the
   * queue rather than overtake what is already waiting.
   */
  #settleWrite(): void {
    this.#writeInFlight = false;
    let next = this.#writeQueue.shift();
    while (next?.superseded) next = this.#writeQueue.shift();
    if (next) this.#sendWrite(next);
    else this.#completeFlushWaitersIfIdle();
  }

  #completeFlushWaitersIfIdle(): void {
    if (this.#persistTimer || this.#writeInFlight || this.#writeQueue.length > 0) return;
    const waiters = [...this.#flushWaiters];
    this.#flushWaiters.clear();
    for (const waiter of waiters) waiter.settle(!waiter.failed);
  }

  /**
   * The aanmelding as the STORE holds it right now. `fallback` covers the row the store never had:
   * the review-inbox panel binds its own aanmelding through `props`.
   */
  #storeRow(moduleId: string, entryId: string, fallback: Entry): Entry {
    return this.#modulesStore.entries()[moduleId]?.find(entry => entry.id === entryId) ?? fallback;
  }

  /**
   * What a row currently holds for `keys` inside this field's own value. A key the row does not
   * carry reads as `undefined`, which is how `#writeOwnValueToStore` removes it again on rollback.
   */
  #ownValueSlice(row: Entry, keys: readonly string[]): Record<string, unknown> {
    const ownValue = this.#storedOwnValue(row);
    return Object.fromEntries(keys.map(key => [key, ownValue[key]]));
  }

  /**
   * Merge this field's own sub-keys into the CURRENT store row, and return what was written.
   *
   * Merged, not replaced, for the same reason the server PATCH deep-merges: a count-only write
   * carries no `declineMessage`, and replacing the object outright would blank it in the store
   * while the server still has it. A key whose value is `undefined` is REMOVED instead of stored
   * as undefined — that is how a rollback restores a sub-key the row never carried.
   */
  #writeOwnValueToStore(
    moduleId: string,
    entryId: string,
    values: Record<string, unknown>,
    fallback: Entry,
    etag?: string
  ): Entry {
    const current = this.#storeRow(moduleId, entryId, fallback);
    const ownValue = this.#storedOwnValue(current);
    for (const [key, value] of Object.entries(values)) {
      if (value === undefined) delete ownValue[key];
      else ownValue[key] = value;
    }
    const updated: Entry = {
      ...current,
      ...(etag ? { _etag: etag, etag } : {}),
      entry: {
        archived: current.entry?.archived ?? false,
        ...(current.entry ?? {}),
        [this.#fieldKey()]: ownValue,
      },
    };
    this.#modulesStore.updateEntryState({ moduleId, entry: updated });
    // The review inbox owns a query-local collection, so it cannot observe this mutation through
    // the global module bucket. Publish the same row to keep action visibility and the action's
    // input entry aligned with the optimistic write (and with its rollback on failure).
    this.#reviewWriteRegistry.publishEntry(updated);
    return updated;
  }

  /* ── Signals seeded from Formly properties in ngOnInit ────────────────── */

  /**
   * Mirrors of Formly's non-reactive properties. `field`/`options`/`formControl`
   * become available only after the FieldType is wired by Formly, so reading
   * them inside `computed()` once would snapshot `null` and never update.
   * We snapshot them in `ngOnInit` and expose as signals.
   */
  readonly #aanmeldingState = signal<Entry | null>(null);
  readonly #onboardingValue = signal<Entry | null>(null);

  /**
   * Detail-page context, when present. On the aanmelding detail page the field is generated
   * from the module's `generatedForm`, which cannot carry an `aanmelding` prop — the envelope
   * lives in the page's DetailsService instead (same optional-token pattern as tabs/route/
   * inline-entries). Null in the review-inbox panel, where props.aanmelding is the source.
   */
  readonly #detailsService = inject(DETAILS_SERVICE_TOKEN, { optional: true });

  /* ── Section 1: Aanvrager ─────────────────────────────────────────────── */

  readonly aanmelding = computed<Entry | null>(() => {
    const snapshot = this.#aanmeldingState() ?? this.#detailsService?.entry() ?? null;
    if (!snapshot?.id || !snapshot.moduleId) return snapshot;
    return this.#modulesStore.entries()[snapshot.moduleId]?.find(entry => entry.id === snapshot.id) ?? snapshot;
  });

  readonly applicantFields = computed<ApplicantField[]>(() => {
    const e = this.aanmelding()?.entry;
    if (!e) return [];
    const user = e['user'];
    return [
      { label: 'Naam', value: user?.entry?.general?.name?.fullName ?? user?.selectedInput ?? null },
      { label: 'E-mail', value: user?.entry?.general?.email ?? e['emailAdress'] ?? null },
      { label: 'Bedrijf', value: e['company']?.selectedInput ?? null },
      { label: 'Vestiging', value: e['location']?.selectedInput ?? null },
      { label: 'Onboarding-formulier', value: e['onboarding-form']?.selectedInput ?? null },
      { label: 'Contract', value: e['contract']?.selectedInput ?? null },
      { label: 'Status', value: e['status']?.name ?? null },
    ];
  });

  /* ── Section 2 & 3: Wizard pages ──────────────────────────────────────── */

  readonly onboardingEntry = this.#onboardingValue.asReadonly();

  /**
   * `generatedForm` fetched separately from the onboarding-forms module, used when the
   * onboarding entry only carries a thin `{ id, selectedInput }` reference. Null while the
   * embedded envelope is usable (or before a fetch resolves).
   */
  readonly #fetchedGeneratedForm = signal<FormlyFieldConfig[] | null>(null);
  readonly #formFetchState = signal<'idle' | 'loading' | 'failed'>('idle');
  /** Id whose fetch already ran (or is in flight) — keeps re-binds from re-fetching. */
  readonly #fetchedFormId = signal<string | null>(null);
  /** Aanmelding whose reverse onboarding-entry lookup already ran (or is in flight). */
  readonly #lookedUpAanmeldingId = signal<string | null>(null);

  /** True while we have an id but the inner `entry` payload hasn't arrived yet. */
  readonly isLoadingForm = computed<boolean>(() => {
    const entry = this.onboardingEntry();
    if (!!entry?.id && !entry.entry) return true;
    return this.#formFetchState() === 'loading';
  });

  /**
   * Raw wizard pages from the form definition. Computed once and partitioned downstream into
   * answerSections (non-documenten) — the documenten-page is surfaced separately as section 3.
   */
  /**
   * Raw persisted form definition. The reference is an envelope with the full
   * onboarding-forms entry on some aanmeldingen and a thin `{ id, selectedInput }` on others
   * (#20715) — fall back to the separately fetched definition when the envelope is missing.
   */
  readonly #generatedFormSource = computed<FormlyFieldConfig[]>(() => {
    const entry = this.onboardingEntry();
    const embedded = entry?.entry?.['onboarding-form']?.entry?.form?.generatedForm;
    const generatedForm = Array.isArray(embedded) && embedded.length > 0 ? embedded : this.#fetchedGeneratedForm();
    return Array.isArray(generatedForm) ? generatedForm : [];
  });

  /**
   * Persisted Formly field id → props.keyPath, for translating legacy id-style
   * `compareField`s ('formly_…', the shape findFieldValueById resolves on the LIVE field
   * tree) into keyPaths the static walk can evaluate against the entry.
   *
   * `fieldArray` is deliberately NOT walked (see `field-tree.util.ts`) — the live resolver
   * never descends it, so an id only reachable there must stay unresolvable here too (fail-open
   * downstream).
   */
  readonly #conditionKeyPathById = computed<Map<string, string>>(() => {
    const byId = new Map<string, string>();
    forEachInFieldTree(this.#generatedFormSource(), field => {
      const keyPath = field.props?.keyPath;
      // FIRST-wins on duplicate ids, mirroring searchFieldGroupsForValue: sub-id
      // composition and copied source fields make duplicates structurally expected, and a
      // last-wins map would evaluate against a different field than the live form does.
      if (typeof field.id === 'string' && field.id && typeof keyPath === 'string' && keyPath && !byId.has(field.id)) {
        byId.set(field.id, keyPath);
      }
    });
    return byId;
  });

  readonly #processedPages = computed<ProcessedPage[]>(() => {
    const generatedForm = this.#generatedFormSource();
    if (generatedForm.length === 0) return [];

    // The persisted definition is data, not a type: a legacy or half-migrated document can carry
    // a `fieldGroup` that is not an array or a null element. Skip those instead of throwing —
    // a throw inside a computed is cached by Angular and re-thrown on every read, which takes
    // the whole review card down for that entry.
    const pages: ProcessedPage[] = [];
    for (const field of generatedForm) {
      if (!field || typeof field !== 'object') continue;
      // Single-wizard top-level case: walk into its pages.
      if (Array.isArray(field.fieldGroup)) {
        // If the wrapper itself has a label and no nested wizard pages, treat it
        // as one section.
        const innerHasPages = field.fieldGroup.some(p => Array.isArray(p?.fieldGroup) && p.props?.label);
        if (innerHasPages) {
          for (const page of field.fieldGroup) {
            if (Array.isArray(page?.fieldGroup)) {
              pages.push({
                label: String(page.props?.label ?? ''),
                fields: page.fieldGroup,
                key: page.key,
                props: page.props,
                className: page.className,
                hide: page.hide,
                // `field` is the root-level wrapper (wizard/tabs/pages/group) one level above
                // every page it carries.
                wrapperProps: field.props,
              });
            }
          }
        } else if (field.props?.label) {
          pages.push({
            label: String(field.props.label),
            fields: field.fieldGroup,
            key: field.key,
            props: field.props,
            className: field.className,
            hide: field.hide,
            // `field` IS the root-level entry here — no separate wrapper above it.
          });
        }
      }
    }
    return pages;
  });

  /**
   * Wizard pages excluding the documenten-page, flattened to answered Label-list rows
   * (Figma contract §4 — same presentation as the Aanvrager section). Unanswered fields and
   * pages without any answer are dropped entirely; "—" placeholder rows would bury the actual
   * answers under form-shaped noise on partially filled aanmeldingen.
   */
  readonly answerSections = computed<AnswerSection[]>(() => {
    const entry = this.onboardingEntry();
    const currentRoleIds = this.#userStore.currentUserRoles();
    // The taxonomy-label cache is OWNED HERE, scoped to this one computation: taxonomies can
    // change between recomputes, so resolved options must never survive past this run, while
    // sibling fields sharing a taxonomy must resolve it once (see createTaxonomyOptionResolver).
    const taxonomyOptionsCache = new Map<string, TaxonomyOption[]>();
    return (
      this.#processedPages()
        .filter(p => !this.#isDocumentsPage(p))
        // A step hidden the way technical helper fields are hidden (hide/className/width) was
        // never shown to the applicant — same rules as the field walk below applies.
        .filter(p => !this.#isTechnicallyHiddenPage(p))
        // The live wizard hides conditionally configured STEPS too — a hidden step's stale
        // answers must not read as real answers, same rule as per field below. Page
        // conditions evaluate CONSERVATIVELY (pageLevel): a dropped section is strictly
        // worse than a dropped row.
        .filter(p => !this.#hiddenByCondition({ props: p.props } as FormlyFieldConfig, entry, true))
        .map(p => ({
          label: p.label,
          target: pageTarget(pageKeyOf(p)),
          // The wrapper's and the page's own props seed the ancestor chain, mirroring
          // FormService.addExpressionsAndHooks/iterateFields, which accumulates ancestorProps
          // from the true generatedForm root: a role-based right denied on the wizard/tabs/pages/
          // group wrapper, OR on the page itself, must flow down to every field on it — the same
          // way it flows into a nested group below.
          rows: this.#extractAnswerRows(
            p.fields,
            entry,
            currentRoleIds,
            [p.wrapperProps, p.props],
            taxonomyOptionsCache
          ),
        }))
        .filter(s => s.rows.length > 0)
    );
  });

  #isTechnicallyHiddenPage(page: ProcessedPage): boolean {
    return isTechnicallyHidden(page.hide, page.className, page.props?.width);
  }

  /**
   * The wizard step section 3 renders itself. Decided by what the step CONTAINS (the shared
   * `hostsDocumentsChecklist`, which the carrier's wizard and status page use to find the very same
   * step), not by a key or label reading 'documenten': that made the exclusion depend on wording a
   * tenant is free to change, so a step named "Uploads" showed up as answer rows here as well —
   * next to the documents section already rendering its uploads.
   */
  #isDocumentsPage(page: ProcessedPage): boolean {
    return hostsDocumentsChecklist({ fieldGroup: page.fields as WizardPageFieldTree[] });
  }

  /**
   * Flatten a wizard page's fields into answered {label, value} rows, walking nested groups.
   *
   * `ancestorProps` is the chain of `props` objects from the page down to (but excluding) the
   * current field — the same accumulator `FormService.addExpressionsAndHooks`/`mergedHideFunction`
   * build while walking `fieldGroup`/`fieldArray`. It lets `effectiveFieldRightForHierarchy` narrow
   * a role-based read-right the same way the live form does: a right denied on a parent group
   * flows down to every field inside it, even one with no `hasRoleBasedRights` config of its own.
   */
  #extractAnswerRows(
    fields: FormlyFieldConfig[],
    entry: Entry | null,
    currentRoleIds: readonly string[],
    ancestorProps: readonly unknown[],
    taxonomyOptionsCache: Map<string, TaxonomyOption[]>
  ): AnswerRow[] {
    const rows: AnswerRow[] = [];
    for (const field of fields) {
      if (!field || typeof field !== 'object') continue;
      const type = String(field.type ?? '');
      if (isNonAnswerFieldType(type)) continue;

      // Fields the applicant never saw (technical helpers hidden via class or width) have no
      // place in the review either.
      if (isTechnicallyHidden(field.hide, field.className, field.props?.width)) continue;

      // The live form hides conditional fields via runtime expressions the persisted form cannot
      // carry; evaluate the same persisted conditional/conditions props against the entry here,
      // so a stale answer from an inactive wizard branch never reads as a real answer.
      if (this.#hiddenByCondition(field, entry)) continue;

      const fieldAncestorProps = [...ancestorProps, field.props];

      // Re-apply the live form's per-field role-based read right: the pre-rewrite component ran
      // the generated form through FormService.addExpressionsAndHooks, whose `hide` expression
      // (mergedHideFunction) hides a field the current user's roles may not read. This static walk
      // must fail closed the same way, or a field marked read-restricted in the form builder
      // renders its answer to every admin opening this card.
      if (!canReadFieldRight(effectiveFieldRightForHierarchy(fieldAncestorProps, currentRoleIds))) continue;

      const label = this.#fieldLabel(field);

      // Recurse only into layout containers and unlabeled wrappers (wizard-review parity via
      // the shared CONTAINER_TYPES). A LABELED non-container field with a fieldGroup is a
      // composite LEAF — name, address, date_range, geolocation, … — one row, never one per
      // sub-key; generatedForm grows fieldGroups on eleven such leaf types, so an allow-list
      // of composites can never be complete.
      if (
        Array.isArray(field.fieldGroup) &&
        field.fieldGroup.length &&
        (!label || CONTAINER_TYPES.has(field.type as string))
      ) {
        rows.push(
          ...this.#extractAnswerRows(field.fieldGroup, entry, currentRoleIds, fieldAncestorProps, taxonomyOptionsCache)
        );
        continue;
      }

      if (!label) continue;

      const raw = this.#fieldValue(field, entry);
      // Masking of secret types happens inside the shared formatter.
      const value = formatDisplayValue(raw, {
        type,
        options: field.props?.options,
        optionsArray: field.props?.optionsArray,
        resolveOptionLabel: createTaxonomyOptionResolver(
          this.#modulesStore,
          field.props?.optionsArray,
          taxonomyOptionsCache
        ),
        addonLeft: (field.props?.['addonLeft'] as { text?: string } | undefined)?.text,
        addonRight: (field.props?.['addonRight'] as { text?: string } | undefined)?.text,
      });
      if (value === null) continue;

      const fullWidth = type === 'textarea' || value.length > 48;
      rows.push({ label, value, fullWidth });
    }
    return rows;
  }

  /**
   * Mirrors the live form's conditional hide for the static walk: FormService's
   * `hideConditionalFunction` is reused verbatim on a minimal probe whose root-form value is the
   * onboarding entry's data — the same value the live expression reads (`getValueOfRootForm`
   * plus the `formState.entry` fallback). Fields without the persisted conditional/conditions
   * props are never hidden here.
   */
  #hiddenByCondition(field: FormlyFieldConfig, entry: Entry | null, pageLevel = false): boolean {
    if (field.props?.conditional !== true || !Array.isArray(field.props?.conditions)) return false;

    // Legacy conditions name their compare field by persisted Formly id — the live form
    // resolves those via findFieldValueById on the real field tree, which the probe does not
    // have. Any id the map knows translates to the target field's keyPath; a reference this
    // form does not carry AND that no keyPath resolver could ever reach fails OPEN (show the
    // row): a stray extra row beats silently dropping an answer the applicant gave from an
    // approval surface. `isIdReference` is the discriminant rather than the 'formly_'
    // prefix, because nothing enforces that prefix — the form-builder keeps author-set ids
    // verbatim, and one of those left the fail-open branch and was evaluated as a keyPath.
    const byId = this.#conditionKeyPathById();
    const conditions: unknown[] = [];
    for (const condition of field.props.conditions as { compareField?: unknown }[]) {
      const rawCompareField = condition?.compareField;
      const compareField =
        typeof rawCompareField === 'string' && byId.has(rawCompareField) ? byId.get(rawCompareField) : rawCompareField;
      if (compareField !== rawCompareField) {
        conditions.push({ ...condition, compareField });
      } else {
        if (typeof compareField === 'string' && isIdReference(compareField)) {
          return false;
        }
        conditions.push(condition);
      }

      // Page level is CONSERVATIVE: only a compare value that resolves from the entry's own
      // DATA (a persisted answer — stable between fill time and review time) may hide a whole
      // section. An envelope field like `status` only resolves through the formState.entry
      // fallback against the CURRENT entry, while the live wizard read the FILL-TIME entry —
      // hiding on that drift silently drops every answer the applicant gave on the step. The
      // row-level filter inside the section still prunes stale per-field answers.
      if (pageLevel) {
        const resolvesFromData =
          typeof compareField === 'string' &&
          compareField &&
          UtilityService.keyPathToValue(
            { entry: entry?.entry ?? {}, id: entry?.id } as unknown as Entry,
            compareField
          ) !== undefined;
        if (!resolvesFromData) {
          return false;
        }
      }
    }

    const probe = {
      props: { ...field.props, conditions },
      form: { getRawValue: () => entry?.entry ?? {} },
      options: { formState: { entry } },
    } as unknown as FormlyFieldConfig;
    try {
      return this.#formService.hideConditionalFunction()(probe);
    } catch (conditionError) {
      // Fail OPEN: a malformed condition must degrade to one extra row, never blank the whole
      // card — a throw inside the answerSections computed is cached by Angular and re-thrown
      // on every read.
      debugLog('OnboardingOverview', 'conditional evaluation failed, showing the row', conditionError);
      return false;
    }
  }

  #fieldLabel(field: FormlyFieldConfig): string | null {
    const label = field.props?.label;
    if (typeof label !== 'string') return null;
    // Rich labels (e.g. the terms checkbox) can carry HTML — the Label-list row wants plain text.
    const plain = label.replace(/<[^>]*>/g, '').trim();
    return plain || null;
  }

  #fieldValue(field: FormlyFieldConfig, entry: Entry | null): unknown {
    if (!entry) return undefined;
    const keyPath = field.props?.keyPath;
    if (typeof keyPath === 'string' && keyPath) {
      return UtilityService.keyPathToValue(entry, keyPath);
    }
    const key = field.key;
    return typeof key === 'string' ? entry.entry?.[key] : undefined;
  }

  /* ── Section 3: Documenten ────────────────────────────────────────────── */

  /** User id used to scope this component's document-record query. */
  readonly #docsScopeUserId = signal<string | null>(null);
  /**
   * Company id used to scope the same cache, alongside the user id. A document can be anchored
   * to either: `required-documents-checklist.component.ts` (the carrier-facing upload flow this
   * review checks against) resolves BOTH from the aanmelding and ORs them, so a document uploaded
   * against the company alone still counts toward the carrier's checklist. Without the same OR
   * here, such a document is invisible in this review and silently skipped by approve/reject.
   */
  readonly #docsScopeCompanyId = signal<string | null>(null);
  /** Query-local records: a review must never replace the module-wide entries bucket. */
  readonly #documentRecords = signal<readonly Entry[]>([]);
  /** Templates the carrier's checklist shows; loaded together with the records. */
  readonly #visibleTemplateIds = signal<ReadonlySet<string>>(new Set());
  readonly documentsLoading = signal<boolean>(false);

  /**
   * Documents from this component's query-local snapshot. The client-side predicate remains a
   * defence against a malformed/broadened server filter and is shared with transition reads.
   */
  readonly documents = computed<Entry[]>(() => {
    if (!this.documentModuleId()) return [];
    const all = this.#documentRecords();
    const userId = this.#docsScopeUserId();
    if (!userId) return [];
    const companyId = this.#docsScopeCompanyId();
    const templateIds = this.#visibleTemplateIds();
    return all.filter(doc => documentBelongsToReview(doc, userId, companyId, templateIds));
  });

  /*
   * Which records belong to this review — this user's or company's, uploaded against a template the
   * carrier's checklist shows — is `documentBelongsToReview` in `onboarding-checklist-templates.ts`,
   * shared with ReviewDecisionCoordinator so Goedkeuren acts on exactly the list shown here.
   */

  /**
   * A `computed`, not a method: the template reads this once per document row (directly, and
   * again through `documentCardMenu` → `canArchiveDocument`), and a method re-runs the
   * `enabledEntities()` scan on every change-detection pass. The module list only changes when
   * the store reloads, so memoization collapses the whole loop to one lookup.
   */
  readonly documentModuleId = computed<Module['id'] | null>(
    () => this.#modulesStore.enabledEntities().find(m => m.code === DOCUMENT_MODULE_CODE)?.id ?? null
  );

  /**
   * Record how the document load ended. `succeeded: true` also covers "there was nothing to load"
   * (no user link, no document module): the list is then legitimately empty, and the count
   * correction and any parked transition must run on that empty list instead of waiting forever
   * for a load that is never coming — which left Goedkeuren hidden with no way to reach it.
   */
  #settleDocumentLoad(succeeded: boolean): void {
    this.documentsLoading.set(false);
    this.#documentsLoadSucceeded.set(succeeded);
  }

  /**
   * Fetches every page into component-local state. No filtered review query writes to or cancels
   * the global module entry bucket.
   */
  #loadDocuments(
    userId: string,
    companyId: string | null,
    onboardingFormRef: Entry | undefined,
    generation: number
  ): void {
    const moduleId = this.documentModuleId();
    if (!moduleId) {
      debugLog('OnboardingOverview', 'documents.fetch.skipped', 'no document-records module id');
      this.#settleDocumentLoad(true);
      return;
    }
    // Company OR user, like the carrier's checklist: a company-only upload counts there too.
    const filter = reviewDocumentsFilter(userId, companyId);
    debugLog('OnboardingOverview', 'documents.fetch.start', { moduleId, filter });

    this.documentsLoading.set(true);
    forkJoin([
      this.#reviewDocumentsQuery.loadAll({ moduleId, filter, pageSize: DOCUMENT_RECORDS_PAGE_SIZE }),
      this.#checklistTemplates.visibleTemplateIds(onboardingFormRef),
    ])
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe({
        next: ([entries, templateIds]) => {
          if (generation !== this.#documentsRequestGeneration) return;
          this.#visibleTemplateIds.set(templateIds);
          this.#documentRecords.set(entries);
          this.#settleDocumentLoad(true);
        },
        error: error => {
          if (generation !== this.#documentsRequestGeneration) return;
          console.error('Documentrecords laden voor onboarding-review mislukt:', error);
          this.#documentRecords.set([]);
          this.#settleDocumentLoad(false);
        },
      });
  }

  /** Unify status-slug across data shapes. Missing status = 'pending'. */
  #documentStatusSlug(doc: Entry): 'approved' | 'rejected' | 'expired' | 'pending' {
    const slug = doc.entry?.['status']?.slug;
    if (slug === 'approved' || slug === 'rejected' || slug === 'expired') return slug;
    return 'pending';
  }

  documentTitle(doc: Entry): string {
    return resolveDocumentTitle(doc);
  }

  documentBadge(doc: Entry): DocumentCardBadge {
    return deriveCardBadge({
      state: this.#documentStatusSlug(doc),
      endDate: (doc.entry?.['end-date'] as string | null | undefined) ?? null,
    });
  }

  documentReviewComment(doc: Entry): string | null {
    return (doc.entry?.['review-comment'] as string | undefined) ?? null;
  }

  /**
   * Who judged the record and when, for the review tile's status row (A-Beoordeling). The interface
   * actions write `reviewed-by` as a user reference and `reviewed-at` as a date.
   */
  documentReviewedBy(doc: Entry): string | null {
    return formatDisplayValue(doc.entry?.['reviewed-by']);
  }

  documentReviewedAt(doc: Entry): string | null {
    return (doc.entry?.['reviewed-at'] as string | undefined) ?? null;
  }

  /**
   * The review tile (A-Beoordeling) is config-only on this surface: the field's `documentsDisplayMode`
   * set to `tile`, like the checklist's; any other value keeps the comfortable review card.
   */
  documentCardDensity(): DocumentCardDensity {
    return this.props?.['documentsDisplayMode'] === 'tile' ? 'tile' : 'comfortable';
  }

  /**
   * Feeds `app-document-card`'s `[file]` input — name plus upload moment, which the card renders
   * as its own "Geüpload dd-MM-yy" meta line. Closes the earlier deviation where this surface's
   * own meta line showed the filename instead of the upload date.
   */
  documentCardFile(doc: Entry): DocumentCardFile | null {
    if (!entryHasFile(doc)) return null;
    return {
      name: resolveDocumentFileName(doc) ?? '',
      uploadedAt: doc.createdAt ?? null,
      // The reviewer sees who uploaded (A-Beoordeling); the envelope knows them by e-mail only.
      uploadedBy: doc.createdByEmail ?? null,
    };
  }

  /**
   * Label/value lines under the upload row (Figma "Documents & uploads"). Both fields are
   * optional on a document record, and the card drops the empty ones — an unfilled optional
   * field is not a finding the reviewer has to read past. The date runs through the shared
   * review formatter, so a vervaldatum reads exactly as it does in the answer rows above it.
   */
  documentMeta(doc: Entry): DocumentCardMetaRow[] {
    return [
      { label: 'Documentnummer', value: formatDisplayValue(doc.entry?.['document-number']) },
      { label: 'Vervaldatum', value: formatDisplayValue(doc.entry?.['end-date']) },
    ];
  }

  /**
   * Menu offered on the shared card. Preview/download only when the record actually carries a
   * file — the pre-refactor buttons had the same entryHasFile gate, and offering them on a
   * file-less record just clicks through to an error notify. History is always reachable;
   * archive keeps the same create-permission gate as before.
   */
  documentCardMenu(doc: Entry): DocumentCardMenu {
    const hasFile = entryHasFile(doc);
    return {
      preview: hasFile,
      download: hasFile,
      history: true,
      archive: this.canArchiveDocument(),
    };
  }

  downloadDocument(doc: Entry): void {
    this.#documentActions.download(doc, this.documentModuleId());
  }

  previewDocument(doc: Entry): void {
    this.#documentActions.preview(doc, this.documentTitle(doc), this.documentModuleId());
  }

  openDocumentHistory(doc: Entry): void {
    const moduleId = this.documentModuleId();
    if (!moduleId || !doc.id) return;
    openHistoryDialog(this.#dialog, { moduleId, entryId: doc.id, title: this.documentTitle(doc) });
  }

  archiveDocument(doc: Entry): void {
    const entity = this.#modulesStore.enabledEntities().find(module => module.code === DOCUMENT_MODULE_CODE);
    if (!entity || !doc.id || !this.canArchiveDocument()) return;
    this.#entriesService.confirmArchive(doc, entity).subscribe();
  }

  /**
   * Also a `computed`: `documentCardMenu` calls it once per document row, and its inputs are
   * both signals (`documentModuleId` and the store's `currentUserPermissions`), so the
   * permission scan still re-runs the moment a takeover changes the roles.
   */
  readonly canArchiveDocument = computed<boolean>(() => {
    const moduleId = this.documentModuleId();
    return !!moduleId && this.#userStore.checkIfHasCreatePermission(moduleId);
  });

  /* ── Lifecycle ────────────────────────────────────────────────────────── */

  ngOnInit(): void {
    // Seed the field's own configuration FIRST: everything below (documents refresh, the count
    // effect, the flag reads) depends on knowing which key this field owns and whether flagging
    // is on at all. `reviewFlagging` is absent on every pre-Task-13 config, so anything but an
    // explicit `false` means enabled.
    this.#fieldKey.set(this.field?.key === undefined || this.field?.key === null ? '' : String(this.field.key));
    this.flaggingEnabled.set(this.field?.props?.['reviewFlagging'] !== false);

    // Seed the parent-aanmelding signal from `field.props.aanmelding` (review-inbox panel).
    // We deliberately avoid Formly's `options.formState` — it isn't reliably
    // propagated to nested FieldType instances and was returning undefined
    // here. `props` is per-field and always synchronously available.
    // On the detail page this prop does not exist; `aanmelding()` then falls back to the
    // optional DetailsService (see #detailsService above).
    const aanmeldingFromProps = (this.field?.props?.['aanmelding'] as Entry | undefined) ?? null;
    this.#aanmeldingState.set(aanmeldingFromProps);
    if (aanmeldingFromProps?.id) {
      this.#unregisterReviewWriter = this.#reviewWriteRegistry.register(aanmeldingFromProps.id, () =>
        this.flushPendingReview()
      );
    }

    // Seed the embedded onboarding-entry from the aanmelding's SIBLING link key. This field's own
    // control carries review state only, so it is not a source for the link any more — see
    // ONBOARDING_LINK_KEY. When the sibling is absent (most aanmeldingen: the forward link is
    // written on the onboarding side) the reverse lookup resolves it instead.
    this.#resolveOnboardingLink();

    // Kick off the documents fetch explicitly. Doing it here (rather than via
    // an effect that tracks signals) avoids any uncertainty about effect
    // timing relative to FieldType property wiring.
    this.#refreshDocuments();

    // React to subsequent formControl changes (e.g. parent re-bind).
    //
    // DetailsService.model is a `computed` over the store entry, so ANY store write on this
    // aanmelding — this field's own review PATCH included — makes Formly re-apply a fresh model
    // and re-emit here. The emission carries this field's OWN value, which is review state:
    // never a link, and never a reason to touch the resolved onboarding entry.
    if (this.formControl) {
      this.formControl.valueChanges.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe(value => {
        // Before the key rename this field's key WAS the link key, so an aanmelding saved back
        // then can still carry an onboarding envelope in its review value. Following it would
        // point section 2 at stale data and re-fetch on every model re-apply; the sibling key is
        // the only link now.
        const legacyLink = this.#asOnboardingLink(value);
        if (legacyLink) {
          debugLog('OnboardingOverview', 'own-value.legacy-link.ignored', { id: legacyLink.id });
        }
        this.#resolveOnboardingLink();
        this.#refreshDocuments();
      });
    }
  }

  /**
   * Resolve section 2's onboarding entry for the CURRENT aanmelding, at most once.
   *
   * Two sources, in order: the aanmelding's sibling link (written by the sync actions — free, no
   * request), and otherwise the reverse lookup by user. Once an entry is resolved for this
   * aanmelding id nothing re-runs: the own control emits on every flag click, and re-resolving
   * per emission was a query (and a form fetch) per click against an entry already in hand.
   */
  #resolveOnboardingLink(): void {
    const aanmeldingId = this.aanmelding()?.id ?? null;
    if (this.onboardingEntry()?.id && this.#resolvedForAanmeldingId === aanmeldingId) return;

    const link = this.#asOnboardingLink(this.aanmelding()?.entry?.[ONBOARDING_LINK_KEY]);
    if (link?.id && link.id !== this.onboardingEntry()?.id) {
      this.#setOnboardingEntry(link);
      // The list-endpoint trims the embedded `entry` payload — with only an id, fetch the full
      // onboarding-entry detail so section 2 can render the wizard answers.
      if (!link.entry) this.#loadOnboardingDetail(link.id);
    }
    this.#resolveOnboardingForm();
    this.#resolveOnboardingEntry();
  }

  /**
   * Store the resolved onboarding entry together with the aanmelding it belongs to, so
   * `#resolveOnboardingLink` can tell "already resolved for THIS request" from "resolved for the
   * previous one" after a re-bind.
   */
  #setOnboardingEntry(entry: Entry | null): void {
    this.#onboardingValue.set(entry);
    this.#resolvedForAanmeldingId = entry?.id ? (this.aanmelding()?.id ?? null) : undefined;
  }

  #refreshDocuments(): void {
    const generation = ++this.#documentsRequestGeneration;
    const userId = this.aanmelding()?.entry?.['user']?.id ?? this.onboardingEntry()?.entry?.['user']?.id;
    // Same resolution `required-documents-checklist.component.ts` uses for its own anchor — the
    // aanmelding's company link, when it has one, so a company-only document is not invisible here.
    const companyId =
      (this.aanmelding()?.entry?.['company']?.id as string | undefined) ??
      (this.onboardingEntry()?.entry?.['company']?.id as string | undefined) ??
      null;
    debugLog('OnboardingOverview', 'documents.refresh', {
      aanmeldingId: this.aanmelding()?.id,
      userId,
      companyId,
    });
    // Set the scope signals so the `documents` computed knows what to filter
    // for once the store-load resolves (or immediately, if the store already
    // holds matching entries from a prior load).
    this.#docsScopeUserId.set(userId ?? null);
    this.#docsScopeCompanyId.set(companyId);
    this.#documentRecords.set([]);
    this.#documentsLoadSucceeded.set(false);
    this.#lastSyncedFlagCount.set(null);
    this.#countSyncRetried = false;
    // A new aanmelding binding invalidates any in-flight optimistic flag edit for the PREVIOUS
    // aanmelding — without this, a flag toggled just before switching would still be sitting in
    // `#localFlags` when `#schedulePersist`'s debounce fires, and would PATCH onto (and render
    // over) the NEW aanmelding instead.
    //
    // Scoped to an actual id CHANGE: this method runs on every `formControl.valueChanges`
    // emission, and a host re-patching the SAME request (writing an unrelated field, say) fires
    // one too. Resetting unconditionally silently dropped a flag toggled inside the debounce
    // window — same class of bug as the transition guard's own id-awareness above.
    const aanmeldingId = this.aanmelding()?.id ?? null;
    if (aanmeldingId !== this.#lastRefreshedAanmeldingId) {
      this.#lastRefreshedAanmeldingId = aanmeldingId;
      this.#cancelScheduledPersist();
      this.#localFlags.set(null);
    }
    if (!userId || !this.documentModuleId()) {
      // Nothing to load — settled, not pending: see `#settleDocumentLoad`.
      this.#settleDocumentLoad(true);
      return;
    }
    const onboardingFormRef =
      this.aanmelding()?.entry?.['onboarding-form'] ?? this.onboardingEntry()?.entry?.['onboarding-form'];
    this.#loadDocuments(userId, companyId, onboardingFormRef, generation);
  }

  #loadOnboardingDetail(entryId: string): void {
    // The onboarding module is a `page`, so it is absent from `enabledEntities()` (entity/module
    // only) — look it up across every enabled module instead.
    const moduleId = this.#modulesStore.enabledModules().find(m => m.code === ONBOARDING_ENTRY_MODULE_CODE)?.id;
    if (!moduleId) return;
    this.#entriesService
      .getEntry(moduleId, entryId)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe({
        next: full => {
          this.#setOnboardingEntry(full ?? null);
          this.#resolveOnboardingForm();
        },
        error: err => console.error('[onboarding-overview] Failed to load onboarding-entry detail:', err),
      });
  }

  /**
   * Recover the onboarding-entry when the aanmelding carries no sibling link value.
   *
   * The link between an aanmelding and the onboarding the carrier actually filled in is written
   * on the onboarding-entry side (`entry['onboarding-aanmelding-id']`); the forward link on the
   * aanmelding stays empty on most records. Without a value the field renders "nog niet
   * ingevuld" even though a completed onboarding exists, so resolve it the other way around.
   *
   * The backend 500s on a `$filter` over the hyphenated `entry.onboarding-aanmelding-id` key, so
   * scope the query by user and match the aanmelding-id client-side. Only an unambiguous result
   * is accepted: an explicit aanmelding-id match, or the user's single onboarding-entry.
   */
  #resolveOnboardingEntry(): void {
    if (this.onboardingEntry()?.id) return; // the field already carries the link

    const aanmelding = this.aanmelding();
    const userId = aanmelding?.entry?.['user']?.id as string | undefined;
    if (!aanmelding?.id || !userId || this.#lookedUpAanmeldingId() === aanmelding.id) return;

    const moduleId = this.#modulesStore.enabledModules().find(m => m.code === ONBOARDING_ENTRY_MODULE_CODE)?.id;
    if (!moduleId) {
      debugLog('OnboardingOverview', 'entry.lookup.skipped', 'no onboarding-entry module id');
      return;
    }

    this.#lookedUpAanmeldingId.set(aanmelding.id);
    this.#formFetchState.set('loading');
    debugLog('OnboardingOverview', 'entry.lookup.start', { moduleId, userId, aanmeldingId: aanmelding.id });
    this.#entriesService
      .getEntries({
        moduleId,
        offset: 0,
        limit: 20,
        filter: `entry.user.id eq '${userId}'`,
        applyQueryParams: false,
      })
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe({
        next: entries => {
          const list = (entries ?? []) as Entry[];
          const match =
            list.find(e => e.entry?.['onboarding-aanmelding-id'] === aanmelding.id) ??
            (list.length === 1 ? list[0] : undefined);
          debugLog('OnboardingOverview', 'entry.lookup.done', { found: list.length, matched: match?.id ?? null });
          this.#formFetchState.set('idle');
          if (!match) return;
          this.#setOnboardingEntry(match);
          this.#resolveOnboardingForm();
        },
        error: err => {
          console.error('[onboarding-overview] Onboarding-entry opzoeken mislukt:', err);
          this.#formFetchState.set('failed');
        },
      });
  }

  /**
   * Make sure section 2 has a `generatedForm` to render.
   *
   * `entry['onboarding-form']` comes in two shapes: a full onboarding-forms envelope
   * (`entry.form.generatedForm` present) on aanmeldingen created through the older flow, and a
   * thin `{ id, selectedInput }` reference on newer ones — the detail endpoint does not hydrate
   * it either. Fetch the onboarding-forms entry by id in the thin case, mirroring the fallback
   * the public onboarding-status page already uses (#20715).
   */
  #resolveOnboardingForm(): void {
    const formRef = this.onboardingEntry()?.entry?.['onboarding-form'];
    const embedded = formRef?.entry?.form?.generatedForm;
    if (Array.isArray(embedded) && embedded.length > 0) {
      this.#formFetchState.set('idle');
      return;
    }

    const formId = formRef?.id as string | undefined;
    if (!formId || this.#fetchedFormId() === formId) return;

    const formsModuleId = this.#modulesStore.getModuleByCode(ONBOARDING_FORMS_MODULE_CODE)?.id;
    if (!formsModuleId) {
      debugLog('OnboardingOverview', 'form.fetch.skipped', 'no onboarding-forms module id');
      return;
    }

    this.#fetchedFormId.set(formId);
    this.#formFetchState.set('loading');
    debugLog('OnboardingOverview', 'form.fetch.start', { formsModuleId, formId });
    this.#entriesService
      .getEntry(formsModuleId, formId)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe({
        next: formEntry => {
          const generatedForm = formEntry?.entry?.form?.generatedForm;
          if (!Array.isArray(generatedForm) || generatedForm.length === 0) {
            console.warn('[onboarding-overview] Onboarding-formulier zonder generatedForm:', formId);
            this.#formFetchState.set('failed');
            return;
          }
          this.#fetchedGeneratedForm.set(generatedForm);
          this.#formFetchState.set('idle');
        },
        error: err => {
          console.error('[onboarding-overview] Onboarding-formulier ophalen mislukt:', err);
          this.#formFetchState.set('failed');
        },
      });
  }
}
